<script setup>
/**
 * Remove a task (field report F3.6): on its row in the list, and on its Check screen.
 *
 * `deleteTask` existed at every layer (client, BFF, sidecar) and no screen called it after the
 * old editor was deleted (fork 2aa58a1bd7), so a task could only ever be switched off.
 *
 * THE CONFIRM IS IN PLACE, the menu editor's pattern, never a browser dialog.
 *
 * IT IS UNDOABLE, AND SAYS SO. The sidecar keeps a removed task as a C2 revision
 * (`scenario-store.ts` `deleteScenario` records `action: 'delete'`), and "Recently removed" on the
 * Tasks page puts it back for the kept period (`helpers/copy-facts.js` `KEPT_FOR`). So the confirm
 * names that, never "permanent", and offers the softer move: switching it off.
 */
import { ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import { useWasloI18n } from '../../i18n.js';
import { wasloApi } from '../../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  taskId: { type: Number, required: true },
  /** On a list row: a small link, and the confirm opens under the row's words. */
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['removed']);

const { t } = useWasloI18n();

const confirming = ref(false);
const isRemoving = ref(false);
const failed = ref(false);

const start = () => {
  confirming.value = true;
  failed.value = false;
};

const cancel = () => {
  confirming.value = false;
};

const remove = async () => {
  if (isRemoving.value || props.accountId === null) return;
  isRemoving.value = true;
  failed.value = false;
  try {
    await wasloApi.deleteTask(props.accountId, props.taskId);
    confirming.value = false;
    emit('removed', props.taskId);
  } catch {
    failed.value = true;
  } finally {
    isRemoving.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col gap-3" :data-waslo-task-remove="taskId">
    <SettingsHeader
      v-if="!compact"
      :heading="t('WASLO.TASKS.REMOVE.HEADING')"
      :description="t('WASLO.TASKS.REMOVE.DESCRIPTION')"
    />
    <div
      v-if="confirming"
      class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-ruby-8 bg-n-solid-1"
      data-waslo-task-remove-box
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.TASKS.REMOVE.CONFIRM') }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.TASKS.REMOVE.CONFIRM_BODY') }}
      </p>
      <p
        v-if="failed"
        class="mb-0 text-sm text-n-ruby-9"
        data-waslo-task-remove-failed
      >
        {{ t('WASLO.TASKS.REMOVE.FAILED') }}
      </p>
      <div class="flex flex-wrap items-center gap-3">
        <Button
          :label="t('WASLO.TASKS.REMOVE.CONFIRM_ACTION')"
          variant="solid"
          color="ruby"
          size="sm"
          :is-loading="isRemoving"
          data-waslo-task-remove-confirm
          @click="remove"
        />
        <Button
          :label="t('WASLO.TASKS.REMOVE.CANCEL')"
          variant="faded"
          color="slate"
          size="sm"
          data-waslo-task-remove-cancel
          @click="cancel"
        />
      </div>
    </div>
    <button
      v-else-if="compact"
      type="button"
      class="self-start text-xs font-medium text-n-ruby-9 hover:underline"
      data-waslo-task-remove-start
      @click="start"
    >
      {{ t('WASLO.TASKS.REMOVE.ACTION_SHORT') }}
    </button>
    <Button
      v-else
      :label="t('WASLO.TASKS.REMOVE.ACTION')"
      icon="i-lucide-trash-2"
      variant="faded"
      color="ruby"
      size="sm"
      class="self-start"
      data-waslo-task-remove-start
      @click="start"
    />
  </section>
</template>
